export const intervalQualities = ['dd', 'm', 'M', 'A', 'd', 'P', 'AA'] as const;
export type IntervalQuality = typeof intervalQualities[number];

export function isIntervalQuality(quality: string): quality is IntervalQuality {
  return intervalQualities.includes(quality as IntervalQuality);
}

export const genericToChromaticIntervalsDictionary: Record<
  SimpleGenericIntervalNumber,
  { [chromaticIntervalClass: number]: IntervalQuality }
> = {
  1: {
    0: 'P',
    1: 'A',
    2: 'AA',
  },
  2: {
    0: 'd',
    1: 'm',
    2: 'M',
    3: 'A',
    4: 'AA',
  },
  3: {
    1: 'dd',
    2: 'd',
    3: 'm',
    4: 'M',
    5: 'A',
    6: 'AA',
  },
  4: {
    3: 'dd',
    4: 'd',
    5: 'P',
    6: 'A',
    7: 'AA',
  },
  5: {
    5: 'dd',
    6: 'd',
    7: 'P',
    8: 'A',
    9: 'AA',
  },
  6: {
    6: 'dd',
    7: 'd',
    8: 'm',
    9: 'M',
    10: 'A',
    11: 'AA',
  },
  7: {
    8: 'dd',
    9: 'd',
    10: 'm',
    11: 'M',
    12: 'A',
    13: 'AA',
  },
  8: {
    10: 'dd',
    11: 'd',
    12: 'P',
    13: 'A',
    14: 'AA',
  },
} as const;

/**
 * Longhand names for shorthand interval qualities.
 */
export const intervalQualityLongNames: Record<IntervalQuality, string> = {
  dd: 'Doubly diminished',
  d: 'diminished',
  m: 'minor',
  M: 'Major',
  P: 'Perfect',
  A: 'Augmented',
  AA: 'Doubly Augmented',
};

export type SimpleGenericIntervalNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
export type CompoundGenericIntervalNumber = 9 | 10 | 11 | 12 | 13 | 14 | 15;

export const majorMinorIntervalGroupQualities = ['dd', 'd', 'm', 'M', 'A', 'AA'] as const;
export type MajorMinorIntervalGroupQuality = typeof majorMinorIntervalGroupQualities[number];

export const perfectIntervalGroupQualities = ['dd', 'd', 'P', 'A', 'AA'] as const;
export type PerfectIntervalGroupQuality = typeof perfectIntervalGroupQualities[number];

export const majorMinorIntervalGroupNumbers = [2, 3, 6, 7, 9, 10, 13, 14] as const;
export type MajorMinorIntervalGroupNumber = typeof majorMinorIntervalGroupNumbers[number];

export const perfectIntervalGroupNumbers = [1, 4, 5, 8, 11, 12, 15] as const;
export type PerfectIntervalGroupNumber = typeof perfectIntervalGroupNumbers[number];

export const intervalDirectionPrefaces = ['-', '', '+'] as const;
export type IntervalDirectionPreface = typeof intervalDirectionPrefaces[number];

export type MajorMinorIntervalGroupName =
  `${IntervalDirectionPreface}${MajorMinorIntervalGroupQuality}${MajorMinorIntervalGroupNumber}`;

export type PerfectIntervalGroupName =
  `${IntervalDirectionPreface}${PerfectIntervalGroupQuality}${PerfectIntervalGroupNumber}`;

export type IntervalName = MajorMinorIntervalGroupName | PerfectIntervalGroupName;

const majorMinorIntervalGroupNameRe = new RegExp(
  `^(\\+|-)?(${majorMinorIntervalGroupQualities.join('|')})(${majorMinorIntervalGroupNumbers.join(
    '|'
  )})$`
);

export function isMajorMinorIntervalGroupName(
  interval: string
): interval is MajorMinorIntervalGroupName {
  return !!majorMinorIntervalGroupNameRe.exec(interval);
}

export function parseMajorMinorIntervalGroupString(interval: MajorMinorIntervalGroupName): {
  direction: 1 | -1;
  quality: MajorMinorIntervalGroupQuality;
  genericInterval: MajorMinorIntervalGroupNumber;
} {
  const parsedInterval = majorMinorIntervalGroupNameRe.exec(interval);
  if (!parsedInterval) throw Error(`${interval} is not a valid Major/minor interval`);
  const [, directionString, qualityString, genericIntervalString] = parsedInterval;
  return {
    direction: directionString && directionString === '-' ? -1 : 1,
    quality: qualityString as MajorMinorIntervalGroupQuality,
    genericInterval: Number(genericIntervalString) as MajorMinorIntervalGroupNumber,
  };
}

const perfectIntervalGroupStringRe = new RegExp(
  `^(\\+|-)?(${perfectIntervalGroupQualities.join('|')})(${perfectIntervalGroupNumbers.join('|')})$`
);

export function isPerfectIntervalGroupName(interval: string): interval is PerfectIntervalGroupName {
  return !!perfectIntervalGroupStringRe.exec(interval);
}

export function parsePerfectIntervalGroupString(interval: PerfectIntervalGroupName): {
  direction: 1 | -1;
  quality: PerfectIntervalGroupQuality;
  genericInterval: PerfectIntervalGroupNumber;
} {
  const parsedInterval = perfectIntervalGroupStringRe.exec(interval);
  if (!parsedInterval) throw Error(`${interval} is not a valid Perfect group interval`);
  const [, directionString, qualityString, genericIntervalString] = parsedInterval;
  return {
    direction: directionString && directionString === '-' ? -1 : 1,
    quality: qualityString as PerfectIntervalGroupQuality,
    genericInterval: Number(genericIntervalString) as PerfectIntervalGroupNumber,
  };
}

export function isIntervalName(interval: string): interval is IntervalName {
  return isPerfectIntervalGroupName(interval) || isMajorMinorIntervalGroupName(interval);
}

export function parseIntervalString(interval: IntervalName) {
  return isMajorMinorIntervalGroupName(interval)
    ? parseMajorMinorIntervalGroupString(interval)
    : parsePerfectIntervalGroupString(interval);
}
